import type { DataSource, Entity, EarthquakeEntity } from '../types/entity';

/**
 * USGS Earthquake API — feed GeoJSON público, sem autenticação.
 * Usamos o feed "M2.5+, últimas 24h" como padrão: abaixo disso o volume
 * global é grande demais pro propósito de dashboard de situação.
 * Docs: https://earthquake.usgs.gov/earthquakes/feed/v1.0/geojson.php
 */
const USGS_FEED_URL =
  'https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/2.5_day.geojson';

/** M5.5+ é convenção comum pra "sismo com potencial de dano relevante"
 *  — é o único critério que acende severity: 'critical' nesta camada. */
const CRITICAL_MAGNITUDE_THRESHOLD = 5.5;

interface UsgsFeatureProperties {
  mag: number;
  place: string;
  time: number;
  url: string;
}

interface UsgsFeature {
  properties: UsgsFeatureProperties;
  geometry: {
    type: 'Point';
    // USGS retorna [lon, lat, depthKm]
    coordinates: [number, number, number];
  };
  id: string;
}

interface UsgsFeedResponse {
  features: UsgsFeature[];
}

function toEarthquakeEntity(feature: UsgsFeature): EarthquakeEntity {
  const [lon, lat, depthKm] = feature.geometry.coordinates;
  const { mag, place, time, url } = feature.properties;

  return {
    id: `quake-${feature.id}`,
    layer: 'earthquakes',
    lat,
    lon,
    label: `M${mag.toFixed(1)} — ${place}`,
    severity: mag >= CRITICAL_MAGNITUDE_THRESHOLD ? 'critical' : 'normal',
    observedAt: new Date(time).toISOString(),
    raw: {
      magnitude: mag,
      depthKm,
      place,
      usgsUrl: url,
    },
  };
}

export const usgsSource: DataSource = {
  layer: 'earthquakes',
  label: 'Sismos (USGS)',
  requiresClientKey: false,
  viaCloudFunction: false,

  // Bounds ignorados de propósito: o feed já vem filtrado por
  // magnitude/janela de tempo, e o volume diário (dezenas, não milhares)
  // não justifica filtrar client-side por área visível.
  async fetchEntities(): Promise<Entity[]> {
    const res = await fetch(USGS_FEED_URL);
    if (!res.ok) {
      throw new Error(`USGS respondeu ${res.status}`);
    }

    const data = (await res.json()) as UsgsFeedResponse;
    return data.features.map(toEarthquakeEntity);
  },
};
